import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import useAxiosClient from "../axios-client.js";
import "./MarketplaceSettings.css";

const PALETTE = [
  { bg: "rgba(217,119,6,.16)", fg: "#f59e0b" },
  { bg: "rgba(56,189,248,.16)", fg: "#38bdf8" },
  { bg: "rgba(52,211,153,.16)", fg: "#34d399" },
  { bg: "rgba(236,72,153,.16)", fg: "#ec4899" },
  { bg: "rgba(139,92,246,.16)", fg: "#8b5cf6" },
  { bg: "rgba(251,191,36,.16)", fg: "#fbbf24" },
  { bg: "rgba(244,114,182,.16)", fg: "#f472b6" },
];

const hash = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 100000;
  return h;
};
const tileColor = (id) => PALETTE[hash(String(id)) % PALETTE.length];
const avatarColor = (id) => PALETTE[(hash(String(id)) + 1) % PALETTE.length];

const initials = (u) => {
  const name = u.name || `${u.firstname || ""} ${u.lastname || ""}`.trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const fullName = (u) => u.name || `${u.firstname || ""} ${u.lastname || ""}`.trim();
const displayLink = (link) => (link || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
const roleLabel = (u) => {
  const r = typeof u.role === "string" ? u.role : (u.roles && u.roles[0]) || "";
  if (!r) return "USER";
  if (r === "SUDO") return "SUDO";
  if (r.toLowerCase() === "admin") return "MANAGER";
  return r.toUpperCase();
};

const MarketplaceSettings = () => {
  const axiosClient = useAxiosClient();
  const [marketplaces, setMarketplaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const filterRef = useRef(null);

  // -- users for the slide-over ----------------------------------------------
  const [users, setUsers] = useState([]);

  // -- slide-over -------------------------------------------------------------
  const [slideover, setSlideover] = useState(null); // { mode: 'create' } | { mode: 'edit', mp }
  const [name, setName] = useState("");
  const [pageLink, setPageLink] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [peopleSearch, setPeopleSearch] = useState("");
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // -- inline rename ----------------------------------------------------------
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef(null);

  // -- inline delete ----------------------------------------------------------
  const [deleting, setDeleting] = useState(null); // { mp, value, error }
  const [savingDelete, setSavingDelete] = useState(false);

  // -- data -------------------------------------------------------------------
  const fetchMarketplaces = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.post("/settings/marketplace/index");
      setMarketplaces(res.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [axiosClient]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axiosClient.get("/users/getRoleUsers");
      setUsers(res.data || []);
    } catch {
      /* team access list just stays empty */
    }
  }, [axiosClient]);

  useEffect(() => {
    fetchMarketplaces();
    fetchUsers();
  }, [fetchMarketplaces, fetchUsers]);

  useEffect(() => {
    if (renamingId && renameInputRef.current) renameInputRef.current.focus();
  }, [renamingId]);

  // -- "/" focuses the filter, Esc closes open panels --------------------------
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !renamingId && !slideover && !deleting) {
        const tag = document.activeElement?.tagName;
        if (tag !== "INPUT" && tag !== "TEXTAREA") {
          e.preventDefault();
          filterRef.current?.focus();
        }
      }
      if (e.key === "Escape") {
        if (deleting) setDeleting(null);
        else if (renamingId) cancelRename();
        else if (slideover) setSlideover(null);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && slideover) {
        e.preventDefault();
        saveSlideover();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // -- slide-over helpers -----------------------------------------------------
  const openCreate = () => {
    setName("");
    setPageLink("");
    setSelectedIds([]);
    setPeopleSearch("");
    setFormError(null);
    setSlideover({ mode: "create" });
  };
  const openEdit = (mp) => {
    setName(mp.name);
    setPageLink(mp.pageLink || "");
    setSelectedIds((mp.users || []).map((u) => u.id));
    setPeopleSearch("");
    setFormError(null);
    setSlideover({ mode: "edit", mp });
  };

  const saveSlideover = async () => {
    if (!name.trim() || !pageLink.trim()) {
      setFormError("Name and page link are required.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = { name: name.trim(), pageLink: pageLink.trim(), users: selectedIds };
      const route = slideover.mode === "edit"
        ? `/settings/marketplace/update/${slideover.mp.id}`
        : "/settings/marketplace/store";
      const res = await axiosClient.post(route, payload);
      toast.success(res.data.message);
      setSlideover(null);
      fetchMarketplaces();
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // -- inline rename ----------------------------------------------------------
  const startRename = (mp) => {
    setRenamingId(mp.id);
    setRenameValue(mp.name);
  };
  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };
  const confirmRename = async () => {
    const val = renameValue.trim();
    if (!val) return;
    setSaving(true);
    try {
      const res = await axiosClient.post(`/settings/marketplace/update/${renamingId}`, {
        name: val,
        pageLink: marketplaces.find((m) => m.id === renamingId)?.pageLink,
        users: (marketplaces.find((m) => m.id === renamingId)?.users || []).map((u) => u.id),
      });
      toast.success(res.data.message);
      cancelRename();
      fetchMarketplaces();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  // -- inline delete ----------------------------------------------------------
  const startDelete = (mp) => setDeleting({ mp, value: "", error: null });
  const confirmDelete = async () => {
    const mp = deleting.mp;
    if ((mp.ordersCount || 0) > 0 && deleting.value.trim() !== mp.name) {
      setDeleting({ ...deleting, error: "The name doesn't match." });
      return;
    }
    setSavingDelete(true);
    try {
      const res = await axiosClient.delete(`/settings/marketplace/delete/${mp.id}`);
      toast.success(res.data.message);
      setDeleting(null);
      fetchMarketplaces();
    } catch (error) {
      setDeleting((d) => ({ ...d, error: error.response?.data?.message || error.message }));
    } finally {
      setSavingDelete(false);
    }
  };

  // -- derived ----------------------------------------------------------------
  const totalAssigned = marketplaces.reduce((sum, m) => sum + (m.users?.length || 0), 0);
  const filteredMarketplaces = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return marketplaces;
    return marketplaces.filter((m) => m.name.toLowerCase().includes(q) || displayLink(m.pageLink).toLowerCase().includes(q));
  }, [marketplaces, filter]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const filteredPeople = useMemo(() => {
    const q = peopleSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => fullName(u).toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
    );
  }, [users, peopleSearch]);
  const selectedPeople = filteredPeople.filter((u) => selectedSet.has(u.id));
  const otherPeople = filteredPeople.filter((u) => !selectedSet.has(u.id));

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };
  const openLink = (link) => {
    const url = /^https?:\/\//.test(link) ? link : `https://${link}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderCard = (mp) => {
    const isRenaming = renamingId === mp.id;
    const isDeleting = deleting && deleting.mp.id === mp.id;
    const tile = tileColor(mp.id);
    const assigned = mp.users || [];
    const overflow = assigned.length - 5;
    const hasOrders = (mp.ordersCount || 0) > 0;

    if (isDeleting) {
      return (
        <div key={mp.id} className="mp-card mp-card--danger">
          <div className="mp-delete-head">
            <div className="mp-tile" style={{ background: "rgba(248,113,113,.16)", color: "var(--mp-err)" }}>
              {mp.name.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div className="mp-delete-title">Delete {mp.name}?</div>
              <div className="mp-delete-link">{displayLink(mp.pageLink)}</div>
            </div>
          </div>

          <div className="mp-delete-blast">
            {assigned.length > 0 && (
              <div className="mp-delete-blast-row">
                <span>·</span>
                <div>
                  <b>{assigned.length} assigned user{assigned.length !== 1 ? "s" : ""}</b> lose access to {hasOrders ? "these orders." : "this page."}
                </div>
              </div>
            )}
            {hasOrders ? (
              <div className="mp-delete-blast-row">
                <span>·</span>
                <div>
                  <b>{mp.ordersCount.toLocaleString()} existing orders</b> keep their full history but lose their marketplace attribution.
                </div>
              </div>
            ) : (
              <div className="mp-delete-blast-row">
                <span>·</span>
                <div>No orders reference it, so nothing else changes.</div>
              </div>
            )}
          </div>

          {hasOrders && (
            <div className="mp-delete-type">
              <div className="mp-delete-type-label">
                Type <code>{mp.name}</code> to confirm.
              </div>
              <div className="mp-type-input">
                <input
                  autoFocus
                  value={deleting.value}
                  onChange={(e) => setDeleting({ ...deleting, value: e.target.value, error: null })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") confirmDelete();
                    if (e.key === "Escape") setDeleting(null);
                  }}
                />
                <span className="mp-type-caret" />
              </div>
              {deleting.error && <div className="mp-field-hint mp-field-hint--err">{deleting.error}</div>}
            </div>
          )}

          <div className="mp-delete-actions">
            <button className="mp-btn-ghost" onClick={() => setDeleting(null)}>
              Cancel
            </button>
            <button
              className="mp-btn-danger"
              onClick={confirmDelete}
              disabled={(hasOrders && deleting.value.trim() !== mp.name) || savingDelete}
            >
              {savingDelete ? "Deleting…" : "Delete marketplace"}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div key={mp.id} className={`mp-card ${isRenaming ? "mp-card--renaming" : ""}`}>
        <div className="mp-card-top">
          <div className="mp-tile" style={{ background: tile.bg, color: tile.fg }}>
            {mp.name.charAt(0).toUpperCase()}
          </div>
          <div className="mp-card-title-wrap">
            {isRenaming ? (
              <div className="mp-inline-input">
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") confirmRename();
                    if (e.key === "Escape") cancelRename();
                  }}
                />
                <span className="mp-caret" />
              </div>
            ) : (
              <>
                <div className="mp-card-name-row">
                  <span className="mp-card-name" title={mp.name}>{mp.name}</span>
                  <button className="mp-card-edit" title="Rename" onClick={() => startRename(mp)}>
                    ✎
                  </button>
                </div>
                <div className="mp-card-added">Added {mp.createdAt || "—"}</div>
              </>
            )}
          </div>
        </div>

        <div className="mp-link-bar">
          <span className="mp-link">{displayLink(mp.pageLink)}</span>
          <button className="mp-link-btn" title="Copy link" onClick={() => copyLink(mp.pageLink)}>
            ⧉
          </button>
          <button className="mp-link-btn" title="Open page" onClick={() => openLink(mp.pageLink)}>
            ↗
          </button>
        </div>

        {assigned.length > 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="mp-avatars">
              {assigned.slice(0, 5).map((u) => {
                const c = avatarColor(u.id);
                return (
                  <span key={u.id} className="mp-avatar" style={{ background: c.bg, color: c.fg }} title={fullName(u)}>
                    {initials(u)}
                  </span>
                );
              })}
              {overflow > 0 && <span className="mp-avatar--overflow">+{overflow}</span>}
            </div>
            <span className="mp-member-label">
              {assigned.length} {assigned.length === 1 ? "person" : "people"}
            </span>
          </div>
        ) : (
          <div className="mp-unassigned-warn">
            <span>▲</span>
            <div>
              <b>Nobody assigned.</b> Orders from this page are visible to no one and cannot be created against it.
            </div>
          </div>
        )}

        <div className="mp-card-foot">
          <div className="mp-orders">
            <b>{(mp.ordersCount || 0).toLocaleString()}</b> orders
          </div>
          <div className="mp-foot-actions">
            <button className="mp-text-btn" onClick={() => openEdit(mp)}>
              Edit
            </button>
            <button className="mp-text-btn mp-text-btn--danger" onClick={() => startDelete(mp)}>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mp-page">
      {/* header */}
      <div className="mp-header">
        <div className="mp-header-left">
          <h1>Marketplaces</h1>
          <p>Shop pages orders arrive from, and the people who can see and use them.</p>
        </div>
        <div className="mp-header-right">
          <div className="mp-filter">
            <span className="mp-filter-icon">⌕</span>
            <input
              ref={filterRef}
              placeholder="Filter marketplaces"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            {filter ? (
              <span className="mp-filter-icon" style={{ cursor: "pointer" }} onClick={() => setFilter("")}>
                ✕
              </span>
            ) : (
              <span className="mp-filter-shortcut">/</span>
            )}
          </div>
          <div className="mp-stats">
            <b>{marketplaces.length}</b> marketplaces
            <span className="mp-stats-sep">·</span>
            <b>{totalAssigned}</b> people assigned
          </div>
          <button className="mp-btn-primary" onClick={openCreate}>
            + New Marketplace
          </button>
        </div>
      </div>

      {/* grid */}
      <div className="mp-grid">
        {loading &&
          [0, 1, 2].map((i) => (
            <div key={i} className="mp-skeleton" style={{ animationDelay: `${i * 0.15}s` }}>
              <div className="mp-skeleton-line" style={{ width: "55%" }} />
              <div className="mp-skeleton-line" style={{ width: "75%" }} />
              <div className="mp-skeleton-line" style={{ width: "40%" }} />
              <div className="mp-skeleton-line" style={{ width: "90%" }} />
            </div>
          ))}

        {!loading && filter && filteredMarketplaces.length === 0 && (
          <div className="mp-empty">
            <div className="mp-empty-title">No marketplace matches “{filter}”</div>
            <div className="mp-empty-sub">Searched {marketplaces.length} marketplaces.</div>
            <div className="mp-empty-actions">
              <button className="mp-btn-ghost" onClick={() => setFilter("")}>
                Clear filter
              </button>
            </div>
          </div>
        )}

        {!loading && !filter && marketplaces.length === 0 && (
          <div className="mp-empty">
            <div className="mp-empty-title">No marketplaces yet</div>
            <div className="mp-empty-sub">
              Connect the shop pages orders arrive from, then assign the people who work them.
            </div>
            <div className="mp-empty-actions">
              <button className="mp-btn-primary" onClick={openCreate}>
                + New Marketplace
              </button>
            </div>
          </div>
        )}

        {!loading && filteredMarketplaces.map(renderCard)}
      </div>

      {/* slide-over */}
      {slideover && (
        <>
          <div className="mp-overlay" onClick={() => setSlideover(null)} />
          <div className="mp-slideover">
            <div className="mp-so-header">
              <div>
                <div className="mp-so-title">
                  {slideover.mode === "edit" ? "Edit marketplace" : "New marketplace"}
                </div>
                <div className="mp-so-sub">
                  {slideover.mode === "edit"
                    ? `${slideover.mp.name} · ${(slideover.mp.ordersCount || 0).toLocaleString()} orders`
                    : "A shop page, and the people who work its orders."}
                </div>
              </div>
              <button className="mp-so-close" onClick={() => setSlideover(null)}>
                ✕
              </button>
            </div>

            <div className="mp-so-body">
              {/* details */}
              <div className="mp-so-section">
                <div className="mp-so-label">Details</div>
                <div className="mp-field">
                  <div className="mp-field-label">Name</div>
                  <div className={`mp-field-input ${formError && !name.trim() ? "mp-field-input--err" : ""}`}>
                    <input
                      autoFocus={slideover.mode === "create"}
                      placeholder="e.g. Dhaka Handloom House"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mp-field">
                  <div className="mp-field-label">Page link</div>
                  <div className={`mp-field-input mp-field-input--mono ${formError && !pageLink.trim() ? "mp-field-input--err" : ""}`}>
                    <input
                      placeholder="https://facebook.com/…"
                      value={pageLink}
                      onChange={(e) => setPageLink(e.target.value)}
                    />
                  </div>
                  <div className="mp-field-hint mp-field-hint--ok">
                    {pageLink.trim() ? `Links to ${displayLink(pageLink)}` : "The public page customers order from."}
                  </div>
                </div>
                {formError && <div className="mp-field-hint mp-field-hint--err">{formError}</div>}
              </div>

              {/* team access */}
              <div className="mp-so-access">
                <div className="mp-so-access-head">
                  <div className="mp-so-label">Team access</div>
                  <div style={{ font: "500 11.5px var(--mp-mono)", color: "var(--dim)" }}>
                    {selectedIds.length} of {users.length} selected
                  </div>
                </div>
                <div className="mp-so-access-note">
                  Assigned people can see this marketplace's orders and create new ones against it. Nobody else can.
                </div>
                <div className="mp-so-search">
                  <span className="mp-filter-icon">⌕</span>
                  <input
                    placeholder="Search name or email"
                    value={peopleSearch}
                    onChange={(e) => setPeopleSearch(e.target.value)}
                  />
                </div>
                <div className="mp-so-select-row">
                  <button
                    className="mp-so-link"
                    onClick={() => setSelectedIds(filteredPeople.map((u) => u.id))}
                  >
                    Select all
                  </button>
                  <span className="mp-so-sep">|</span>
                  <button className="mp-so-link mp-so-link--dim" onClick={() => setSelectedIds([])}>
                    Clear
                  </button>
                </div>

                <div className="mp-people-list">
                  {selectedPeople.length > 0 && (
                    <>
                      <div className="mp-people-group">Assigned</div>
                      {selectedPeople.map((u) => (
                        <div
                          key={u.id}
                          className="mp-person-row mp-person-row--selected"
                          onClick={() => setSelectedIds(selectedIds.filter((id) => id !== u.id))}
                        >
                          <span className="mp-check mp-check--on">✓</span>
                          <PersonCell u={u} accent />
                        </div>
                      ))}
                    </>
                  )}
                  {(selectedPeople.length > 0 || slideover.mode === "edit") && (
                    <div className="mp-people-group">Everyone else</div>
                  )}
                  {otherPeople.map((u) => (
                    <div
                      key={u.id}
                      className="mp-person-row"
                      onClick={() => setSelectedIds([...selectedIds, u.id])}
                    >
                      <span className="mp-check" />
                      <PersonCell u={u} />
                    </div>
                  ))}
                  {filteredPeople.length === 0 && (
                    <div style={{ padding: 14, font: "400 12px var(--mp-font)", color: "var(--dim)" }}>
                      No people match "{peopleSearch}".
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mp-so-footer">
              <span className="mp-so-kbd">Esc cancel · ⌘↵ save</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="mp-btn-ghost" onClick={() => setSlideover(null)}>
                  Cancel
                </button>
                <button className="mp-btn-primary" style={{ height: 34 }} onClick={saveSlideover} disabled={saving}>
                  {saving
                    ? "Saving…"
                    : slideover.mode === "edit"
                    ? "Save changes"
                    : "Create marketplace"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const PersonCell = ({ u, accent = false }) => {
  const c = avatarColor(u.id);
  return (
    <>
      <span className="mp-person-avatar" style={{ background: c.bg, color: c.fg }}>
        {initials(u)}
      </span>
      <span className="mp-person-info">
        <div className="mp-person-name">{fullName(u)}</div>
        <div className="mp-person-email">{u.email}</div>
      </span>
      <span className={`mp-role-badge ${accent ? "mp-role-badge--accent" : ""}`}>
        {roleLabel(u)}
      </span>
    </>
  );
};

export default MarketplaceSettings;
